import React from 'react';
import {Formik, Form, Field} from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import {DashboardLayout, VideoUpload} from '../../components';
import {Button, ButtonVariant, ButtonSize, ButtonType} from '../../components/button';
import {CommunityMultiSelect} from '../../components/community-multi-select';
import {FormControl} from '../../components/form-control';
import {Input} from '../../components/input';
import {JobPreviewModal} from '../../components/job-preview-modal';
import {SkillsInput} from '../../components/skills-input';
import {Select} from '../../components/select';
import {departmentOptions, educationOptions, employmentTypeOptions, experienceLevelOptions, visibilityOptions} from '../../constants';
import {CreateOpportunityFormData, OpportunityVisibility} from '../../models/opportunity';
import {useCreateOpportunity} from './use-create-opportunity';

const validationSchema = Yup.object().shape({
  applicationDeadline: Yup.date()
    .required('Application deadline is required')
    .min(moment().add(1, 'day').toDate(), 'Application deadline must be at least 24 hours from now'),
  department: Yup.string().required('Department is required'),
  educationRequirements: Yup.string().required('Education requirements are required'),
  employmentType: Yup.string().required('Employment type is required'),
  experienceLevel: Yup.string().required('Experience level is required'),
  jobDescription: Yup.string().required('Job description is required'),
  jobTitle: Yup.string().required('Job title is required'),
  location: Yup.string().required('Location is required'),
  requiredSkills: Yup.array().min(1, 'At least one skill is required'),
  restrictedToCommunityIds: Yup.array().when('visibility', {
    is: OpportunityVisibility.PRIVATE,
    then: schema => schema.min(1, 'Please select at least one community.'),
  }),
  salary: Yup.string().required('Salary is required'),
  visibility: Yup.string().required('Visibility is required'),
});

const initialValues: CreateOpportunityFormData = {
  applicationDeadline: '',
  department: '',
  educationRequirements: '',
  employmentType: '',
  experienceLevel: '',
  jobDescription: '',
  jobTitle: '',
  location: '',
  requiredSkills: [],
  restrictedToCommunityIds: [],
  salary: '',
  visibility: OpportunityVisibility.PUBLIC,
};

const getFieldError = (fieldName: keyof CreateOpportunityFormData, touched: any, errors: any) => {
  return touched[fieldName] ? errors[fieldName] || null : null;
};

const CreateOpportunity: React.FC = () => {
  const {
    communities,
    communitiesLoading,
    videoFile,
    showPreview,
    previewData,
    isPublishing,
    isSavingDraft,
    uploadProgress,
    isUploading,
    uploadStatus,
    handleSubmit,
    handleCancel,
    handleVideoUpload,
    handleBackToEdit,
    handleCloseModal,
    handleSaveAsDraft,
    handlePublish,
    handleRemoveVideo,
    handleVideoUploadInModal,
  } = useCreateOpportunity();

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-120px)] overflow-hidden flex flex-col">
        <div className="border-b border-gray-100 flex-shrink-0">
          <div className="max-w-4xl mx-auto px-6 py-2">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Create New Opportunity</h1>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-6">
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
              {({errors, touched}) => (
                <Form id="opportunity-form" className="space-y-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Job Details</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormControl label="Job Title" required error={getFieldError('jobTitle', touched, errors)}>
                        <Field
                          as={Input}
                          name="jobTitle"
                          placeholder="e.g., Senior Robotics Engineer"
                          error={touched.jobTitle && !!errors.jobTitle}
                        />
                      </FormControl>

                      <FormControl label="Department" required error={getFieldError('department', touched, errors)}>
                        <Field
                          as={Select}
                          name="department"
                          options={departmentOptions}
                          placeholder="Select department"
                          error={touched.department && !!errors.department}
                        />
                      </FormControl>

                      <FormControl label="Employment Type" required error={getFieldError('employmentType', touched, errors)}>
                        <Field
                          as={Select}
                          name="employmentType"
                          options={employmentTypeOptions}
                          placeholder="Select Type"
                          error={touched.employmentType && !!errors.employmentType}
                        />
                      </FormControl>

                      <FormControl label="Location" required error={getFieldError('location', touched, errors)}>
                        <Field
                          as={Input}
                          name="location"
                          placeholder="e.g., San Fransisco or Remote"
                          error={touched.location && !!errors.location}
                        />
                      </FormControl>

                      <FormControl label="Experience Level" required error={getFieldError('experienceLevel', touched, errors)}>
                        <Field
                          as={Select}
                          name="experienceLevel"
                          options={experienceLevelOptions}
                          placeholder="Select Level"
                          error={touched.experienceLevel && !!errors.experienceLevel}
                        />
                      </FormControl>

                      <FormControl label="Salary" required error={getFieldError('salary', touched, errors)}>
                        <Field as={Input} name="salary" placeholder="e.g., $80,000 - $ 120,000" error={touched.salary && !!errors.salary} />
                      </FormControl>

                      <FormControl label="Application Deadline" required error={getFieldError('applicationDeadline', touched, errors)}>
                        <Field
                          as={Input}
                          name="applicationDeadline"
                          type="date"
                          min={moment().add(1, 'day').format('YYYY-MM-DD')}
                          placeholder="mm/dd/yyyy"
                          error={touched.applicationDeadline && !!errors.applicationDeadline}
                        />
                      </FormControl>
                    </div>

                    <FormControl label="Job Description" required error={getFieldError('jobDescription', touched, errors)} className="mt-6">
                      <Field name="jobDescription">
                        {({field}: any) => (
                          <textarea
                            {...field}
                            rows={6}
                            placeholder="Describe the role, responsibilities, and what makes this opportunity exciting..."
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none resize-none"
                          />
                        )}
                      </Field>
                    </FormControl>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Skill Requirement</h2>

                    <div className="grid gap-6">
                      <FormControl
                        label="Required Skills"
                        required
                        error={touched.requiredSkills && typeof errors.requiredSkills === 'string' ? errors.requiredSkills : null}>
                        <Field name="requiredSkills">
                          {({field, form}: any) => (
                            <SkillsInput
                              value={field.value || []}
                              onChange={skills => form.setFieldValue('requiredSkills', skills)}
                              placeholder="E.g., Data analytics, Project management, Sales, ReactJS"
                              error={touched.requiredSkills && !!errors.requiredSkills}
                            />
                          )}
                        </Field>
                      </FormControl>

                      <FormControl label="Education Requirements" required error={getFieldError('educationRequirements', touched, errors)}>
                        <Field
                          as={Select}
                          name="educationRequirements"
                          options={educationOptions}
                          placeholder="Select degree"
                          error={touched.educationRequirements && !!errors.educationRequirements}
                        />
                      </FormControl>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Visibility</h2>
                    <div className="border-b border-gray-200 pb-6 mb-6">
                      <FormControl label="Who can see this opportunity?" required error={getFieldError('visibility', touched, errors)}>
                        <Field
                          as={Select}
                          name="visibility"
                          options={visibilityOptions}
                          placeholder="Select visibility"
                          error={touched.visibility && !!errors.visibility}
                        />
                      </FormControl>
                    </div>

                    <Field name="visibility">
                      {({field: visibilityField}: {field: {value: string}}) =>
                        visibilityField.value === OpportunityVisibility.PRIVATE && (
                          <>
                            <h3 className="text-base font-medium text-gray-900 mb-1">Communities</h3>
                            <div className="border-b border-gray-200 pb-4 mb-4" />
                            <FormControl
                              label="Restrict to communities"
                              required
                              description="Only members of these communities will be able to view and apply."
                              error={
                                touched.restrictedToCommunityIds && typeof errors.restrictedToCommunityIds === 'string'
                                  ? errors.restrictedToCommunityIds
                                  : null
                              }>
                              <Field name="restrictedToCommunityIds">
                                {({field, form}: {field: {value: string[]}; form: {setFieldValue: (n: string, v: string[]) => void}}) =>
                                  communitiesLoading ? (
                                    <p className="text-sm text-gray-500">Loading communities...</p>
                                  ) : (
                                    <CommunityMultiSelect
                                      communities={communities}
                                      value={field.value ?? []}
                                      onChange={ids => form.setFieldValue('restrictedToCommunityIds', ids)}
                                      error={touched.restrictedToCommunityIds && !!errors.restrictedToCommunityIds}
                                    />
                                  )
                                }
                              </Field>
                            </FormControl>
                          </>
                        )
                      }
                    </Field>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Employer Video</h2>

                    <VideoUpload videoFile={videoFile} onVideoUpload={handleVideoUpload} onRemoveVideo={handleRemoveVideo} />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex-shrink-0">
          <div className="max-w-4xl mx-auto flex justify-end space-x-4 p-6">
            <Button variant={ButtonVariant.Secondary} size={ButtonSize.SM} onClick={handleCancel} type={ButtonType.BUTTON}>
              Cancel
            </Button>
            <Button variant={ButtonVariant.Primary} size={ButtonSize.SM} form="opportunity-form" type={ButtonType.SUBMIT}>
              Preview
            </Button>
          </div>
        </div>

        {/* Job Preview Modal */}
        {previewData && (
          <JobPreviewModal
            formData={previewData}
            isOpen={showPreview}
            isPublishing={isPublishing}
            isSavingDraft={isSavingDraft}
            isUploading={isUploading}
            onBackToEdit={handleBackToEdit}
            onClose={handleCloseModal}
            onPublish={handlePublish}
            onRemoveVideo={handleRemoveVideo}
            onSaveAsDraft={handleSaveAsDraft}
            onVideoUpload={handleVideoUploadInModal}
            restrictedToCommunities={communities.filter(c => previewData.restrictedToCommunityIds?.includes(c.id))}
            uploadProgress={uploadProgress}
            uploadStatus={uploadStatus}
            videoFile={videoFile}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default CreateOpportunity;
